import type { TFunction } from 'i18next';
import type { PublicIdentity } from '@adieuu/shared';
import type { DisplayMessage } from '../../hooks/useConversations';
import type { DecryptedConversation } from '../../hooks/conversations/types';
import type { MemberSettingsMap } from '../../services/conversationCryptoService';
import { parsePayload } from '../../services/messagePayload';
import { type ChatItem, isSameDay } from './conversationUtils';
import { isUnverifiedSignature } from '../../components/messaging/messageSignatureTrust';

/** Appends optimistic send rows after the newest message, adding a day separator if needed. */
export function appendPendingSendItems(items: ChatItem[], pendingRows: DisplayMessage[]): ChatItem[] {
  if (pendingRows.length === 0) return items;
  const out = [...items];
  for (const msg of pendingRows) {
    const currDate = new Date(msg.createdAt);
    let prevMsgDate: Date | null = null;
    for (let i = out.length - 1; i >= 0; i--) {
      const it = out[i]!;
      if (it.type === 'message') {
        prevMsgDate = new Date(it.msg.createdAt);
        break;
      }
    }
    if (!prevMsgDate || !isSameDay(prevMsgDate, currDate)) {
      out.push({ type: 'day-separator', date: currDate, key: `day-${msg.id}` });
    }
    out.push({ type: 'message', msg, key: msg.id });
  }
  return out;
}

export function getReversedVisibleMessages(
  activeMessages: DisplayMessage[],
  showArtifacts: boolean,
): DisplayMessage[] {
  return [...activeMessages]
    .reverse()
    .filter((msg) => {
      if (showArtifacts) return true;
      if (msg.messageType === 'system') return true;
      if (msg.deleted) return false;
      if (!msg.decryptedContent && msg.decryptionError) return false;
      return true;
    });
}

/**
 * Header avatars: same order as the members list (`participants` array).
 * Group with exactly two people: both members (stack of 2). Larger groups: first 3 members.
 * DM: the sole other in 1:1; otherwise up to 3 other participants in list order.
 */
export function getToolbarAvatarMemberIds(
  conversationType: 'dm' | 'group',
  participants: string[],
  identityId: string | undefined,
): string[] {
  if (participants.length === 0) return [];
  if (conversationType === 'group') {
    if (participants.length === 2) {
      return participants.slice(0, 2);
    }
    return participants.slice(0, 3);
  }
  const others = participants.filter((p) => p !== identityId);
  if (others.length === 0) return [];
  if (others.length === 1) {
    return [others[0]!];
  }
  return others.slice(0, 3);
}

/**
 * Conversation list avatars: same member-order rules as the toolbar, without needing message state.
 */
export function getSidebarListAvatarMemberIds(
  isGroup: boolean,
  participants: string[],
  identityId: string | undefined,
): string[] {
  if (!isGroup) {
    const others = participants.filter((p) => p !== identityId);
    if (others.length === 0) return [];
    return [others[0]!];
  }
  if (participants.length === 0) return [];
  if (participants.length === 2) {
    return participants.slice(0, 2);
  }
  return participants.slice(0, 3);
}

export function getLastMessagePreviewText(activeMessages: DisplayMessage[]): string | undefined {
  for (let i = activeMessages.length - 1; i >= 0; i--) {
    const msg = activeMessages[i]!;
    if (!msg.decryptedContent || msg.deleted || msg.messageType === 'system') continue;
    if (isUnverifiedSignature(msg)) continue;
    const { text } = parsePayload(msg.decryptedContent);
    if (text) return text;
  }
  return undefined;
}

export function buildMessagesByIdMap(
  activeMessages: DisplayMessage[],
  replyParentHydrationMap: Record<string, DisplayMessage>,
): Map<string, DisplayMessage> {
  const m = new Map<string, DisplayMessage>();
  for (const msg of activeMessages) {
    m.set(msg.id, msg);
  }
  for (const [msgId, msg] of Object.entries(replyParentHydrationMap)) {
    if (!m.has(msgId)) {
      m.set(msgId, msg);
    }
  }
  return m;
}

export function buildFlatChatItems(
  reversedMessages: DisplayMessage[],
  unreadCount: number,
  nowMs: number,
): ChatItem[] {
  const items: ChatItem[] = [];
  const unreadIdx =
    unreadCount > 0 && unreadCount < reversedMessages.length
      ? reversedMessages.length - unreadCount
      : -1;

  for (let i = 0; i < reversedMessages.length; i++) {
    const msg = reversedMessages[i]!;
    if (msg.expiresAt && new Date(msg.expiresAt).getTime() <= nowMs) continue;

    const currDate = new Date(msg.createdAt);
    const prevItem = items.length > 0 ? items[items.length - 1] : null;
    const prevMsgDate = prevItem?.type === 'message' ? new Date(prevItem.msg.createdAt) : null;
    const showDaySep = !prevMsgDate || !isSameDay(prevMsgDate, currDate);

    if (showDaySep) {
      items.push({ type: 'day-separator', date: currDate, key: `day-${msg.id}` });
    }
    items.push({ type: 'message', msg, key: msg.id, isFirstUnread: i === unreadIdx || undefined });
  }
  return items;
}

export function resolveToolbarParticipantName(
  pid: string,
  memberSettings: MemberSettingsMap,
  participantProfiles: Record<string, PublicIdentity>,
): string {
  const nickname = memberSettings[pid]?.nickname;
  if (nickname) return nickname;
  const profile = participantProfiles[pid];
  return profile?.displayName ?? profile?.username ?? pid;
}

export function getConversationHeaderCopy(
  conversation: DecryptedConversation,
  identityId: string | undefined,
  participantProfiles: Record<string, PublicIdentity>,
  memberSettings: MemberSettingsMap,
  t: TFunction,
): {
  otherParticipantIds: string[];
  displayName: string;
  subtitle: string;
} {
  const otherParticipantIds = conversation.participants.filter((p) => p !== identityId);
  const resolveToolbarName = (pid: string) =>
    resolveToolbarParticipantName(pid, memberSettings, participantProfiles);

  const displayName =
    conversation.type === 'group'
      ? (conversation.decryptedName ?? t('conversations.group', 'Group'))
      : conversation.decryptedName?.trim()
        ? conversation.decryptedName.trim()
        : otherParticipantIds.map(resolveToolbarName).join(', ');

  const subtitle =
    conversation.type === 'group'
      ? `${conversation.participants.length} ${t('conversations.members', 'members')}`
      : t('conversations.directMessage', 'Direct message');

  return { otherParticipantIds, displayName, subtitle };
}

export const TOOLBAR_PIN_PREVIEW_MAX = 70;

/**
 * Truncated preview for the header “latest pin” line (user message text or fallbacks).
 */
export function formatPinPreviewForToolbar(
  message: DisplayMessage | undefined,
  t: TFunction
): string {
  if (!message) return t('conversations.headerLatestPinLoading', 'Loading…');
  if (message.deleted) {
    return t('conversations.headerLatestPinUnavailable', 'Pinned message unavailable');
  }
  if (message.messageType === 'system') {
    return t('conversations.headerLatestPinSystem', 'System message');
  }
  if (isUnverifiedSignature(message)) {
    return t('conversations.signatureUnverifiedPreview', 'Unverified message');
  }
  const raw = message.decryptedContent ?? '';
  if (!raw) return t('conversations.headerLatestPinLoading', 'Loading…');
  const { text } = parsePayload(raw);
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (!cleaned) return t('conversations.pinnedMessage', 'Pinned');
  if (cleaned.length <= TOOLBAR_PIN_PREVIEW_MAX) return cleaned;
  return `${cleaned.slice(0, TOOLBAR_PIN_PREVIEW_MAX)}…`;
}

/**
 * Matches server rules: DMs — any participant; groups — admins (with legacy createdBy fallback).
 */
export function canManageConversationPinsView(
  conversation: DecryptedConversation | undefined,
  identityId: string | undefined,
): boolean {
  if (!conversation || !identityId) return false;
  if (!conversation.participants.includes(identityId)) return false;
  if (conversation.type === 'dm') return true;
  const admins = conversation.admins;
  if (admins?.length) return admins.includes(identityId);
  return conversation.createdBy === identityId;
}
